import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { UnsavedChangingsService } from '@shared/services/unsaved-changings.service';
import { Logger } from '@shared/utils/mix.utils';
import { BehaviorSubject, Subject } from 'rxjs';

export interface IRouterOutletsData {
  primary: string;
  second: string;
}

@Injectable({
  providedIn: 'root',
})
export class RoutesService {
  private _previousUrl = '';
  private _currentUrl = '';
  outletsData$ = new BehaviorSubject<IRouterOutletsData>({ primary: null, second: null });
  private nextNavigationWithSecoundOutlet = false;
  showSecondOutlet$ = new BehaviorSubject<boolean>(false);
  private currentSecondOutletData: IRouterOutletsData = {
    primary: null,
    second: null,
  }
  navigationEnd$ = new Subject<NavigationEnd>();

  constructor(
    private router: Router,
    private unsavedChangingsService: UnsavedChangingsService,
    private route: ActivatedRoute
  ) {
    router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.switchPrimarySecondaryOutlets(event);
        this.updateOutletsData(event.url);
      }
      if (event instanceof NavigationEnd) {
        this.navigationEnd$.next(event);
        this._previousUrl = this._currentUrl;
        this._currentUrl = event.url;
        this.manageSecondOutlet(event);
      }
    });
  }

  init() {
    // just to force to load this service
  }

  get previousUrl() {
    // console.log('Prev URL', this._previousUrl);
    return this._previousUrl;
  }

  get currentUrl() {
    return this._currentUrl;
  }

  updateOutletsData(url: string) {
    let primary, second;
    [ primary, second ] = this.parseUrl(url);
    this.outletsData$.next({ primary, second: second || null });
  }

  switchPrimarySecondaryOutlets(event: NavigationStart) {
    this.showSecondOutlet$.next(event.url.indexOf('second:second') >= 0);
  }

  closeSecondOutlet() {
    // console.log('Close sec outlet', this.currentSecondOutletData.primary);
    this.nextNavigationWithSecoundOutlet = true;
    this.router.navigate([
      {
        outlets: {
          primary: this.currentSecondOutletData.primary,
          second: null,
        },
      },
    ]).then();
    this.currentSecondOutletData = {
      primary: null,
      second: null
    }
  }

  navigateWithSecondOutlet(primary: any, second: any) {
    this.currentSecondOutletData = {
      primary,
      second
    }
    this.nextNavigationWithSecoundOutlet = true;
    this.router.navigate([
      {
        outlets: {
          primary,
          second,
        },
      },
    ]).then();
  }

  /**
   * this method will disable second outlet if navigation called from any source except
   * method navigateWithSecondOutlet()
   */
  private manageSecondOutlet(event: NavigationEnd) {
    if (event.url.indexOf('(second:') > -1) {
      let primary, second;
      [ primary, second ] = this.parseUrl(event.url);

      if (!this.nextNavigationWithSecoundOutlet) {
        // navigation with second outlet has already done, this is next navigation,
        // so we should clear second outlet
        Logger.log('Clear second outlet', event.url, primary);
        this.unsavedChangingsService.unregisterCurrentComponent();
        this.router.navigate([
          {
            outlets: {
              primary,
              second: null,
            },
          },
        ]).then();
      } else {
        this.nextNavigationWithSecoundOutlet = false;
      }
    }
  }

  private parseUrl(url: string) {
    const urlWithoutParams = url.split('?')[0];
    const spl = urlWithoutParams.split('(second:');
    let primary = spl[0];
    if (primary?.startsWith('/')) {
      primary = primary.slice(1);
    }
    let second = spl[1];
    if (second?.startsWith('/')) {
      second = second.slice(1);
    }
    if (second?.endsWith(')')) {
      second = second.slice(0, -1);
    }
    return [ primary, second ];
  }
}
